
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { apiKeys, APP_CONFIG } from '../config';
import { BookingDetails } from '../types';
import { Logger } from './logger';
import { getGWorkspaceAccessToken, getGWorkspaceAuthUrl, exchangeCodeForTokens } from './tokenService';
export { getGWorkspaceAccessToken, getGWorkspaceAuthUrl, exchangeCodeForTokens };

/**
 * Inserts an event into the user's primary Google Calendar.
 * Returns the created event ID.
 */
export const googleCalendarInsert = async (details: BookingDetails): Promise<string> => {
    // Check credentials FIRST to avoid unnecessary token fetch
    const missingKeys: string[] = [];
    if (!apiKeys.googleWorkspace.refreshToken || apiKeys.googleWorkspace.refreshToken.includes('PLACEHOLDER')) {
        missingKeys.push('apiKeys.googleWorkspace.refreshToken');
    }
    if (!apiKeys.GOOGLE_CALENDAR || apiKeys.GOOGLE_CALENDAR.includes('PLACEHOLDER')) {
        missingKeys.push('apiKeys.GOOGLE_CALENDAR');
    }

    const hasApiKey = missingKeys.length === 0;

    if (!hasApiKey) {
        // Mock mode
        const eventId = `evt-${Date.now().toString(36)}`;
        Logger.info(`[MOCK MODE] Google Calendar: Event would be created: ${eventId}. Missing keys: ${missingKeys.join(', ')}`);
        await new Promise(resolve => setTimeout(resolve, 300));
        return eventId;
    }

    // Only fetch token if we have valid credentials
    const accessToken = await getGWorkspaceAccessToken();

    try {
        const startDateTime = `${details.date}T${details.time}:00Z`; // Simplified ISO string
        const endDateTime = new Date(new Date(startDateTime).getTime() + 60 * 60 * 1000).toISOString();

        const event = {
            summary: `Styling Consultation: ${details.name}`,
            location: 'Google Meet',
            description: `Services: ${details.serviceIds.join(', ')}\n\nNotes: ${details.notes || 'None'}`,
            start: {
                dateTime: startDateTime,
                timeZone: 'UTC',
            },
            end: {
                dateTime: endDateTime,
                timeZone: 'UTC',
            },
            attendees: [
                { email: details.email },
                { email: APP_CONFIG.EMAIL_TO }
            ],
            conferenceData: {
                createRequest: {
                    requestId: `req-${Date.now()}`,
                    conferenceSolutionKey: { type: 'hangoutsMeet' }
                }
            }
        };


        const response = await fetch(`https://www.googleapis.com/calendar/v3/calendars/primary/events?conferenceDataVersion=1&key=${apiKeys.GOOGLE_CALENDAR}`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(event)
        });

        if (!response.ok) {
            const error = await response.json();
            const errorMessage = error.error?.message || response.statusText;

            // Provide helpful message for auth errors
            if (response.status === 401 || errorMessage.includes('authentication') || errorMessage.includes('OAuth')) {
                throw new Error(`Google Calendar authentication failed. OAuth 2.0 access token required. Error: ${errorMessage}`);
            }

            throw new Error(`Google Calendar API error: ${errorMessage}`);
        }

        const result = await response.json();
        Logger.info(`Event created successfully in Google Calendar`, { eventId: result.id });
        return result.id;
    } catch (error) {
        Logger.error('Failed to create Google Calendar event', error);
        throw error;
    }
};

/**
 * Generates a Google Meet link for an event.
 * In a real scenario, this is often part of the calendar event creation.
 * We'll simulate fetching it from the event details.
 */
export const getMeetLinkFromEvent = async (eventId: string): Promise<string> => {
    const missingKeys: string[] = [];
    if (!apiKeys.googleWorkspace.refreshToken || apiKeys.googleWorkspace.refreshToken.includes('PLACEHOLDER')) {
        missingKeys.push('apiKeys.googleWorkspace.refreshToken');
    }
    if (!apiKeys.GOOGLE_CALENDAR || apiKeys.GOOGLE_CALENDAR.includes('PLACEHOLDER')) {
        missingKeys.push('apiKeys.GOOGLE_CALENDAR');
    }

    const hasApiKey = missingKeys.length === 0;

    if (!hasApiKey) {
        // Mock mode
        const meetLink = `https://meet.google.com/abc-${Math.random().toString(36).substring(7)}-xyz`;
        Logger.info(`[MOCK MODE] Google Meet: Link would be generated: ${meetLink}. Missing keys: ${missingKeys.join(', ')}`);
        return meetLink;
    }

    try {
        const accessToken = await getGWorkspaceAccessToken();
        const response = await fetch(`https://www.googleapis.com/calendar/v3/calendars/primary/events/${eventId}?key=${apiKeys.GOOGLE_CALENDAR}`, {
            headers: {
                'Authorization': `Bearer ${accessToken}`
            }
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(`Google Calendar API error (fetch event): ${error.error?.message || response.statusText}`);
        }

        const result = await response.json();
        const meetLink = result.conferenceData?.entryPoints?.[0]?.uri;

        if (!meetLink) {
            Logger.warn('No Meet link found in event data, using fallback mock.');
            return `https://meet.google.com/mock-${eventId}`;
        }

        return meetLink;
    } catch (error) {
        Logger.error('Failed to fetch Meet link from event', error);
        throw error;
    }
};

/**
 * Lists image files from Google Drive.
 */
export const listDriveFiles = async (accessToken: string, query: string = "mimeType contains 'image/'"): Promise<any[]> => {
    try {
        const response = await fetch(`https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&fields=files(id, name, mimeType, thumbnailLink, webContentLink)&key=${apiKeys.GOOGLE_CALENDAR}`, {
            headers: {
                'Authorization': `Bearer ${accessToken}`
            }
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(`Google Drive API error (list): ${error.error?.message || response.statusText}`);
        }

        const data = await response.json();
        return data.files || [];
    } catch (error) {
        Logger.error('Failed to list Google Drive files', error);
        throw error;
    }
};

/**
 * Gets metadata for a specific Google Drive file.
 */
export const getDriveFile = async (accessToken: string, fileId: string): Promise<any> => {
    try {
        const response = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}?fields=id, name, mimeType, thumbnailLink, webContentLink&key=${apiKeys.GOOGLE_CALENDAR}`, {
            headers: {
                'Authorization': `Bearer ${accessToken}`
            }
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(`Google Drive API error (get): ${error.error?.message || response.statusText}`);
        }

        return response.json();
    } catch (error) {
        Logger.error(`Failed to fetch Google Drive file: ${fileId}`, error);
        throw error;
    }
};
